import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api, { errMsg } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Tags, Loader2, Check } from 'lucide-react';

export const CategoryPrefixPanel = () => {
  const qc = useQueryClient();
  const { data: cats = [], isLoading } = useQuery({
    queryKey: ['catalog-categories'],
    queryFn: async () => (await api.get('/catalog/categories')).data.data,
  });
  const [edits, setEdits] = useState({}); // name -> prefix (in-progress)
  const [savingName, setSavingName] = useState('');

  const valueFor = (c) => (edits[c.name] !== undefined ? edits[c.name] : (c.code_prefix || ''));

  const save = async (name) => {
    const code_prefix = (valueFor({ name, code_prefix: '' }) || '').toUpperCase();
    setSavingName(name);
    try {
      await api.post('/catalog/categories/prefix', { name, code_prefix });
      await qc.invalidateQueries({ queryKey: ['catalog-categories'] });
      await qc.invalidateQueries({ queryKey: ['next-code'] });
      toast.success(`Saved prefix for ${name}`);
      setEdits((e) => { const n = { ...e }; delete n[name]; return n; });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingName(''); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="category-prefix-panel">
      <div className="mb-1 flex items-center gap-2"><Tags className="h-4 w-4 text-primary" /><p className="text-sm font-semibold">Category code prefixes</p></div>
      <p className="mb-3 text-xs text-muted-foreground">Set a starting code per category so brand-new categories number cleanly. Enter letters (e.g. <b>P</b>) or letters+number (e.g. <b>PAK1000</b>) — the first item then becomes <b>P1A</b> / <b>PAK1000A</b>, incrementing after.</p>
      {isLoading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : cats.length === 0 ? (
        <p className="py-4 text-sm text-muted-foreground">No categories yet. Create items first, then set prefixes here.</p>
      ) : (
        <div className="max-h-[320px] space-y-2 overflow-auto">
          {cats.map((c) => (
            <div key={c.name} className="flex items-center gap-2" data-testid={`category-prefix-row-${c.name}`}>
              <span className="min-w-0 flex-1 truncate text-sm">{c.name}</span>
              <Input value={valueFor(c)} onChange={(e) => setEdits((s) => ({ ...s, [c.name]: e.target.value.toUpperCase() }))}
                placeholder="e.g. P or PAK1000" className="h-9 w-40 rounded-lg uppercase placeholder:normal-case"
                data-testid={`category-prefix-input-${c.name}`} />
              <Button size="sm" variant="secondary" className="h-9 rounded-lg" disabled={savingName === c.name} onClick={() => save(c.name)} data-testid={`category-prefix-save-${c.name}`}>
                {savingName === c.name ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
